export const ROUTE_LANGUAGE_CODES = [
  'hk',
  'cn',
  'en',
  'jp',
  'kr',
  'th',
  'vn',
  'de',
  'fr',
  'it',
  'es',
  'ug',
] as const;

export type RouteLanguageCode = (typeof ROUTE_LANGUAGE_CODES)[number];

export function isRouteLanguageCode(value: string | null | undefined): value is RouteLanguageCode {
  return ROUTE_LANGUAGE_CODES.includes(value as RouteLanguageCode);
}

function normalizeBasePath(basePath = '/') {
  if (!basePath || basePath === '/') return '';

  return `/${basePath.replace(/^\/+|\/+$/g, '')}`;
}

function normalizePathname(pathname = '/') {
  if (!pathname) return '/';
  const [pathWithoutQuery] = pathname.split(/[?#]/);
  return pathWithoutQuery.startsWith('/') ? pathWithoutQuery : `/${pathWithoutQuery}`;
}

function stripBasePath(pathname: string, basePath = '/') {
  const normalizedBase = normalizeBasePath(basePath);
  const normalizedPathname = normalizePathname(pathname);

  if (!normalizedBase) return normalizedPathname;

  if (normalizedPathname === normalizedBase) return '/';

  if (normalizedPathname.startsWith(`${normalizedBase}/`)) {
    const stripped = normalizedPathname.slice(normalizedBase.length);
    return stripped || '/';
  }

  return normalizedPathname;
}

export function getLanguageFromPath(pathname: string, basePath = '/') {
  const pathWithoutBase = stripBasePath(pathname, basePath);
  const firstSegment = pathWithoutBase.split('/').filter(Boolean)[0];

  return isRouteLanguageCode(firstSegment) ? firstSegment : null;
}

export function getPathWithoutLanguage(pathname: string, basePath = '/') {
  const pathWithoutBase = stripBasePath(pathname, basePath);
  const parts = pathWithoutBase.split('/').filter(Boolean);

  if (isRouteLanguageCode(parts[0])) {
    parts.shift();
  }

  return parts.length ? `/${parts.join('/')}` : '/';
}

export function getRouterBasename(basePath = '/', pathname = '/') {
  const normalizedBase = normalizeBasePath(basePath);
  const languageCode = getLanguageFromPath(pathname, basePath);

  if (!languageCode) return normalizedBase || '/';

  return `${normalizedBase}/${languageCode}`.replace(/\/+/g, '/') || '/';
}

export function buildLocalizedPath(languageCode: string, currentPathname: string, basePath = '/') {
  const normalizedBase = normalizeBasePath(basePath);
  const safeLanguageCode = isRouteLanguageCode(languageCode) ? languageCode : 'hk';
  const contentPath = getPathWithoutLanguage(currentPathname, basePath);
  const localizedPath = `${normalizedBase}/${safeLanguageCode}${contentPath === '/' ? '/' : contentPath}`;

  return localizedPath.replace(/\/+/g, '/');
}

export function getHtmlLangFromRouteLanguage(languageCode: string | null | undefined) {
  switch (languageCode) {
    case 'hk':
      return 'zh-Hant-HK';
    case 'cn':
      return 'zh-Hans-CN';
    case 'jp':
      return 'ja';
    case 'kr':
      return 'ko';
    case 'th':
      return 'th';
    case 'vn':
      return 'vi-VN';
    case 'de':
      return 'de';
    case 'fr':
      return 'fr';
    case 'it':
      return 'it';
    case 'es':
      return 'es';
    case 'ug':
      return 'ug-Arab';
    case 'en':
    default:
      return 'en';
  }
}
